import React, { useState } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Fade,
} from "@mui/material";
import {
  EmailOutlined,
  LockOutlined,
  BusinessOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export default function LoginScreen({ setIsLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState(localStorage.getItem("tenant") || "");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const apiUrl = process.env.REACT_APP_API_URL;

    if (!apiUrl) {
      setError("Configuration Error: REACT_APP_API_URL is not set.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${apiUrl}/web-login`,
        { email, password },
        {
          withCredentials: true,
          headers: {
            "X-Tenant": localStorage.getItem("tenant") || ""
          }
        }
      );

      const { user, token } = response.data;
      sessionStorage.setItem("user", JSON.stringify(user));
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("session_valid", "yes");
      setIsLoggedIn(true);
      navigate("/");

    } catch (error) {
      console.error("Login API Error:", error.response || error);
      setError(error.response?.data?.message || "Invalid credentials or network error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <Box
        sx={{
          flex: { xs: 0, md: 3 },
          display: { xs: "none", md: "block" },
          backgroundImage: `url('/login-bg.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.paper",
          px: { xs: 4, md: 6 },
          py: 4,
          boxShadow: { md: "-4px 0 24px rgba(0,0,0,0.1)" },
          zIndex: 1,
        }}
      >
        <Fade in={true} timeout={1000}>
          <Box
            sx={{
              width: "100%",
              maxWidth: 400,
              textAlign: "center",
            }}
          >
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              fontWeight="800"
              sx={{
                background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 1,
              }}
            >
              PeopleHub
            </Typography>
            <Typography variant="body1" color="textSecondary" fontWeight="500">
              Admin Portal
            </Typography>
          </Box>

          {error && (
            <Fade in={!!error}>
              <Box
                sx={{
                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                  color: "#d32f2f",
                  p: 1.5,
                  borderRadius: 2,
                  mb: 3,
                  fontSize: "0.875rem",
                  border: "1px solid rgba(211, 47, 47, 0.2)",
                }}
              >
                {error}
              </Box>
            </Fade>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              required
              label="Tenant Code"
              variant="outlined"
              margin="normal"
              value={tenant}
              onChange={(e) => {
                setTenant(e.target.value);
                localStorage.setItem("tenant", e.target.value);
              }}
              disabled={loading}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessOutlined color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 3 }
                }
              }}
            />

            <TextField
              fullWidth
              required
              label="Email Address"
              variant="outlined"
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined color="action" />
                    </InputAdornment>
                  ),
                  autoComplete: "username",
                  sx: { borderRadius: 3 }
                }
              }}
            />

            <TextField
              fullWidth
              required
              type={showPassword ? "text" : "password"}
              label="Password"
              variant="outlined"
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  autoComplete: "current-password",
                  sx: { borderRadius: 3 }
                }
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 4,
                py: 1.8,
                borderRadius: 3,
                fontSize: "1rem",
                fontWeight: "bold",
                textTransform: "none",
                background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                boxShadow: (theme) => `0 4px 15px ${theme.palette.primary.main}66`,
                "&:hover": {
                  background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                  transform: "translateY(-2px)",
                  boxShadow: (theme) => `0 6px 20px ${theme.palette.primary.main}80`,
                },
                transition: "all 0.3s ease",
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Sign In"
              )}
            </Button>
          </Box>

          <Box sx={{ mt: 4, opacity: 0.7 }}>
            <Typography variant="caption" color="textSecondary">
              Secure Access for Authorized Administrators Only
            </Typography>
          </Box>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
}